import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, useAuth } from "@/hooks/useAuth";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, Loader2, Plus, X } from "lucide-react";

/**
 * DeepenProfile — Astro Monish-style extended intake.
 * Route: /onboarding/deepen
 *
 * Collects (one question per viewport):
 *   1. 3-8 dated life events (month+year+description)
 *   2. Long pattern (optional)
 *   3. Work now and what you want next
 *   4. Main money concern
 *   5. Wellbeing note (non-medical, optional)
 *   6. Up to 3 questions for KALKI to answer in the report
 *   7. Language preference + include spiritual section
 *   8. Two consents
 *
 * Life Report at /app/report locks until this completes.
 */

const INK = "#1C1C1C";
const INDIGO = "#5D00FF";
const FUCHSIA = "#FF2E9A";
const MUTE = "#676773";

const LANGS = [
  { id: "english",  label: "English" },
  { id: "hindi",    label: "Hindi" },
  { id: "gujarati", label: "Gujarati" },
];

export default function DeepenProfile() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Form state
  const [events, setEvents] = useState([{ month: "", year: "", description: "" }]);
  const [longPattern, setLongPattern] = useState("");
  const [workNext, setWorkNext] = useState("");
  const [moneyConcern, setMoneyConcern] = useState("");
  const [wellbeing, setWellbeing] = useState("");
  const [qs, setQs] = useState(["", "", ""]);
  const [language, setLanguage] = useState("english");
  const [includeSpiritual, setIncludeSpiritual] = useState(true);
  const [consentShared, setConsentShared] = useState(false);
  const [consentTend, setConsentTend] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/", { replace: true });
  }, [loading, user, navigate]);

  // Gate: if your-beginning not done, bounce back; if deepen already done, skip.
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const r = await api.get("/profile");
        if (!r.data?.profile_complete) {
          navigate("/onboarding/your-beginning", { replace: true });
        } else if (r.data?.profile_deep_complete) {
          navigate("/app/chat", { replace: true });
        }
      } catch (_) {}
    })();
  }, [user, navigate]);

  const validEventsCount = useMemo(() =>
    events.filter((e) => e.year && e.description && e.description.trim().length > 2).length,
    [events]
  );
  const validQsCount = useMemo(() => qs.filter((q) => q && q.trim().length > 3).length, [qs]);

  const canAdvance = useMemo(() => {
    if (step === 0) return validEventsCount >= 3;
    if (step === 1) return true;        // long pattern optional
    if (step === 2) return workNext.trim().length > 2;
    if (step === 3) return true;        // money/wellbeing optional
    if (step === 4) return validQsCount >= 1;
    if (step === 5) return true;
    if (step === 6) return consentShared && consentTend;
    return false;
  }, [step, validEventsCount, workNext, validQsCount, consentShared, consentTend]);

  const next = () => { if (canAdvance) { setError(""); setStep((s) => s + 1); } };
  const back = () => { setError(""); setStep((s) => Math.max(0, s - 1)); };

  const submit = async () => {
    setSaving(true); setError("");
    try {
      const payload = {
        life_events: events
          .filter((e) => e.year && e.description && e.description.trim())
          .map((e) => ({
            year: parseInt(e.year, 10),
            month: e.month ? parseInt(e.month, 10) : null,
            description: e.description.trim(),
          })),
        long_pattern: longPattern.trim() || null,
        work_now_and_next: workNext.trim(),
        main_money_concern: moneyConcern.trim() || null,
        wellbeing_note: wellbeing.trim() || null,
        questions: qs.map((q) => q.trim()).filter(Boolean).slice(0, 3),
        language,
        include_spiritual: includeSpiritual,
        consent_shared: consentShared,
        consent_tendencies: consentTend,
      };
      await api.post("/onboarding/deepen", payload);
      setStep(7); // success
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Could not save.";
      setError(typeof msg === "string" ? msg : "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center" style={{ background: "#FAFAFC" }}>
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: INDIGO }} />
      </div>
    );
  }

  const TOTAL = 7;

  return (
    <div
      className="relative min-h-[100dvh] flex flex-col"
      style={{
        background: "#FAFAFC",
        backgroundImage: "radial-gradient(#DCE2EA 1px, transparent 1px)",
        backgroundSize: "28px 28px",
        color: INK,
      }}
      data-testid="deepen-profile"
    >
      <header className="flex items-center justify-between px-6 sm:px-10 pt-6">
        <div className="flex items-center gap-2 text-[11px] tracking-[0.4em]" style={{ color: INDIGO }}>
          <span style={{ textShadow: `0 0 10px ${INDIGO}66` }}>✦</span>
          <span>KALKI</span>
        </div>
        {step > 0 && step < 7 && (
          <button onClick={back} data-testid="dp-back"
            className="inline-flex items-center gap-1 text-[11px] tracking-[0.3em] opacity-55 hover:opacity-90"
            style={{ color: MUTE }}>
            <ChevronLeft className="w-3 h-3" /> BACK
          </button>
        )}
      </header>

      {step < 7 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          {Array.from({ length: TOTAL }).map((_, i) => (
            <span key={i} className="rounded-full transition-all"
              style={{
                width: i === step ? 24 : 6, height: 6,
                background: i <= step
                  ? `linear-gradient(90deg, ${INDIGO}, ${FUCHSIA})`
                  : "rgba(28,28,28,0.12)",
              }}
            />
          ))}
        </div>
      )}

      <main className="flex-1 flex flex-col items-center justify-center w-full max-w-[640px] mx-auto px-6 sm:px-10 pb-28 pt-8">
        <AnimatePresence mode="wait">
          {step === 0 && (
            <Frame key="s0" eyebrow="Step 1 of 7" question="Four to eight turning points." subtitle="Studies, moves, jobs, launches, marriage, big wins or losses. At least 3.">
              <EventsEditor events={events} setEvents={setEvents} />
              <div className="mt-3 text-[11px]" style={{ color: validEventsCount >= 3 ? INDIGO : MUTE }}>
                {validEventsCount} / 3 minimum
              </div>
            </Frame>
          )}
          {step === 1 && (
            <Frame key="s1" eyebrow="Step 2 of 7" question="Any long pattern you've noticed?" subtitle="For example: years of effort without income, or recurring health flares. Optional.">
              <textarea
                value={longPattern} onChange={(e) => setLongPattern(e.target.value)}
                data-testid="dp-pattern"
                placeholder="A pattern KALKI should look at…"
                rows={4}
                className="w-full p-4 bg-white rounded-2xl text-[15px] outline-none"
                style={{ border: `1px solid rgba(28,28,28,0.09)`, color: INK }}
              />
            </Frame>
          )}
          {step === 2 && (
            <Frame key="s2" eyebrow="Step 3 of 7" question="Your work now, and what you want next." subtitle="Current role or venture — and the direction you're drawn to.">
              <textarea
                value={workNext} onChange={(e) => setWorkNext(e.target.value)}
                data-testid="dp-work"
                placeholder="e.g. Building an AI product. Want to raise a seed round and lead a small team."
                rows={5} autoFocus
                className="w-full p-4 bg-white rounded-2xl text-[15px] outline-none"
                style={{ border: `1px solid rgba(28,28,28,0.09)`, color: INK }}
              />
            </Frame>
          )}
          {step === 3 && (
            <Frame key="s3" eyebrow="Step 4 of 7" question="Life now — a few lines." subtitle="Both optional. Nothing medical needed.">
              <div className="w-full space-y-3">
                <LabeledField label="Main money concern">
                  <input value={moneyConcern} onChange={(e) => setMoneyConcern(e.target.value)}
                    placeholder="e.g. Managing cash runway" data-testid="dp-money"
                    className="w-full p-3 bg-white rounded-xl text-[14.5px] outline-none"
                    style={{ border: `1px solid rgba(28,28,28,0.09)`, color: INK }} />
                </LabeledField>
                <LabeledField label="Wellbeing — anything to keep in mind">
                  <input value={wellbeing} onChange={(e) => setWellbeing(e.target.value)}
                    placeholder="e.g. Sleep is often disturbed" data-testid="dp-wellbeing"
                    className="w-full p-3 bg-white rounded-xl text-[14.5px] outline-none"
                    style={{ border: `1px solid rgba(28,28,28,0.09)`, color: INK }} />
                </LabeledField>
              </div>
            </Frame>
          )}
          {step === 4 && (
            <Frame key="s4" eyebrow="Step 5 of 7" question="Up to three questions for KALKI." subtitle="These shape the focus of your Life Report. At least one required.">
              <div className="w-full space-y-3">
                {qs.map((q, i) => (
                  <input key={i} value={q}
                    onChange={(e) => setQs((arr) => arr.map((x, j) => j === i ? e.target.value : x))}
                    placeholder={`Question ${i + 1}${i === 0 ? " (required)" : " (optional)"}`}
                    data-testid={`dp-q-${i}`}
                    className="w-full p-3.5 bg-white rounded-xl text-[14.5px] outline-none"
                    style={{ border: `1px solid rgba(28,28,28,0.09)`, color: INK }}
                  />
                ))}
              </div>
            </Frame>
          )}
          {step === 5 && (
            <Frame key="s5" eyebrow="Step 6 of 7" question="How would you like your report?" subtitle="Language and depth.">
              <div className="w-full space-y-5">
                <div>
                  <div className="text-[11px] tracking-[0.26em] uppercase mb-2" style={{ color: MUTE }}>Language</div>
                  <div className="flex gap-2 flex-wrap">
                    {LANGS.map((l) => (
                      <button key={l.id} data-testid={`dp-lang-${l.id}`}
                        onClick={() => setLanguage(l.id)}
                        className="px-5 py-2.5 rounded-full text-[13px] font-medium"
                        style={{
                          background: language === l.id ? `linear-gradient(135deg, ${INDIGO}, ${FUCHSIA})` : "white",
                          border: language === l.id ? "1px solid transparent" : "1px solid rgba(28,28,28,0.09)",
                          color: language === l.id ? "white" : INK,
                        }}>{l.label}</button>
                    ))}
                  </div>
                </div>
                <label className="flex items-start gap-3 cursor-pointer" data-testid="dp-spiritual">
                  <input type="checkbox" checked={includeSpiritual} onChange={(e) => setIncludeSpiritual(e.target.checked)}
                    className="mt-1 w-4 h-4 accent-[#5D00FF]" />
                  <span className="text-[13.5px] leading-[1.55]" style={{ color: INK }}>
                    Include the <strong>Spiritual life lessons</strong> section — soul map, lessons, timeline.
                  </span>
                </label>
              </div>
            </Frame>
          )}
          {step === 6 && (
            <Frame key="s6" eyebrow="Step 7 of 7" question="Two quick confirmations." subtitle="Both required to generate the report.">
              <div className="w-full space-y-4">
                <label className="flex items-start gap-3 cursor-pointer p-4 rounded-2xl" data-testid="dp-consent-shared"
                  style={{ background: "white", border: `1px solid ${consentShared ? INDIGO + "66" : "rgba(28,28,28,0.09)"}` }}>
                  <input type="checkbox" checked={consentShared} onChange={(e) => setConsentShared(e.target.checked)}
                    className="mt-1 w-4 h-4 accent-[#5D00FF]" />
                  <span className="text-[14px] leading-[1.55]" style={{ color: INK }}>
                    The chart is <strong>mine</strong>, or I have the person's <strong>consent</strong> to share their birth details.
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer p-4 rounded-2xl" data-testid="dp-consent-tend"
                  style={{ background: "white", border: `1px solid ${consentTend ? INDIGO + "66" : "rgba(28,28,28,0.09)"}` }}>
                  <input type="checkbox" checked={consentTend} onChange={(e) => setConsentTend(e.target.checked)}
                    className="mt-1 w-4 h-4 accent-[#5D00FF]" />
                  <span className="text-[14px] leading-[1.55]" style={{ color: INK }}>
                    I understand a chart shows <strong>tendencies and timing, not fate</strong>, and this is not medical, legal or financial advice.
                  </span>
                </label>
              </div>
            </Frame>
          )}
          {step === 7 && (
            <SuccessFrame onContinue={() => navigate("/app/chat")} />
          )}
        </AnimatePresence>

        {error && (
          <div className="mt-6 text-[12.5px] text-center" style={{ color: FUCHSIA }} data-testid="dp-error">{error}</div>
        )}

        {step < 6 && step < 7 && (
          <button onClick={next} disabled={!canAdvance} data-testid="dp-continue"
            className="mt-10 inline-flex items-center justify-center gap-2 px-9 py-4 rounded-full text-[13px] font-semibold tracking-[0.14em] uppercase disabled:opacity-30"
            style={{
              background: `linear-gradient(135deg, ${INDIGO}, ${FUCHSIA})`,
              color: "white", boxShadow: `0 16px 42px ${INDIGO}44`, minWidth: 220,
            }}>Continue →</button>
        )}
        {step === 6 && (
          <button onClick={submit} disabled={!canAdvance || saving} data-testid="dp-submit"
            className="mt-10 inline-flex items-center justify-center gap-2 px-9 py-4 rounded-full text-[13px] font-semibold tracking-[0.14em] uppercase disabled:opacity-40"
            style={{
              background: `linear-gradient(135deg, ${INDIGO}, ${FUCHSIA})`,
              color: "white", boxShadow: `0 16px 42px ${INDIGO}44`, minWidth: 260,
            }}>
            {saving ? (<><Loader2 className="w-4 h-4 animate-spin" /> SAVING…</>) : "Deepen my profile →"}
          </button>
        )}
      </main>
    </div>
  );
}

function Frame({ eyebrow, question, subtitle, children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.35, ease: "easeOut" }} className="w-full text-center"
    >
      {eyebrow && <div className="text-[10px] tracking-[0.36em] uppercase mb-5" style={{ color: MUTE }}>✦ {eyebrow}</div>}
      <h2 className="font-serif font-light leading-[1.08] mb-3" style={{ fontSize: "clamp(26px, 4.2vmin, 40px)", color: INK }}>{question}</h2>
      {subtitle && <p className="text-[13.5px] mb-8 max-w-[42ch] mx-auto" style={{ color: MUTE }}>{subtitle}</p>}
      <div className="w-full flex flex-col items-center text-left">{children}</div>
    </motion.div>
  );
}

function LabeledField({ label, children }) {
  return (
    <div className="w-full">
      <div className="text-[10.5px] tracking-[0.26em] uppercase mb-1.5" style={{ color: MUTE }}>{label}</div>
      {children}
    </div>
  );
}

function EventsEditor({ events, setEvents }) {
  const update = (i, patch) => setEvents((arr) => arr.map((e, j) => j === i ? { ...e, ...patch } : e));
  const add = () => events.length < 8 && setEvents((arr) => [...arr, { month: "", year: "", description: "" }]);
  const remove = (i) => setEvents((arr) => arr.length > 1 ? arr.filter((_, j) => j !== i) : arr);

  return (
    <div className="w-full space-y-2.5">
      {events.map((e, i) => (
        <div key={i} className="flex items-center gap-2 p-3 bg-white rounded-xl"
          style={{ border: "1px solid rgba(28,28,28,0.09)" }}>
          <input type="number" min="1" max="12" placeholder="MM" value={e.month}
            data-testid={`dp-ev-${i}-month`}
            onChange={(ev) => update(i, { month: ev.target.value })}
            className="w-14 bg-transparent outline-none text-center text-[13.5px]" style={{ color: INK }} />
          <input type="number" placeholder="YYYY" value={e.year}
            data-testid={`dp-ev-${i}-year`}
            onChange={(ev) => update(i, { year: ev.target.value })}
            className="w-20 bg-transparent outline-none text-center text-[13.5px]" style={{ color: INK }} />
          <input placeholder="What happened" value={e.description}
            data-testid={`dp-ev-${i}-desc`}
            onChange={(ev) => update(i, { description: ev.target.value })}
            className="flex-1 bg-transparent outline-none text-[13.5px]" style={{ color: INK }} />
          <button onClick={() => remove(i)} className="w-7 h-7 rounded-full flex items-center justify-center opacity-50 hover:opacity-100"
            style={{ color: MUTE }}><X className="w-3.5 h-3.5" /></button>
        </div>
      ))}
      {events.length < 8 && (
        <button onClick={add} data-testid="dp-ev-add"
          className="w-full p-3 rounded-xl text-[12.5px] tracking-[0.14em] uppercase flex items-center justify-center gap-2"
          style={{ background: "white", border: `1px dashed ${INDIGO}55`, color: INDIGO }}>
          <Plus className="w-3.5 h-3.5" /> Add another event
        </button>
      )}
    </div>
  );
}

function SuccessFrame({ onContinue }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.45, ease: "easeOut" }}
      className="w-full text-center" data-testid="dp-success"
    >
      <div className="text-[11px] tracking-[0.4em]" style={{ color: INDIGO, textShadow: `0 0 10px ${INDIGO}66` }}>✦ KALKI</div>
      <h2 className="font-serif font-light leading-[1.05] mt-6 mb-4" style={{ fontSize: "clamp(32px, 6vmin, 56px)", color: INK }}>
        Thank you.<br />
        <em className="not-italic" style={{ background: `linear-gradient(135deg, ${INDIGO}, ${FUCHSIA})`, WebkitBackgroundClip: "text", color: "transparent" }}>
          I can now see your story.
        </em>
      </h2>
      <p className="mt-5 max-w-[36ch] mx-auto text-[14.5px] leading-[1.65]" style={{ color: MUTE }}>
        Your Life Report is ready to generate whenever you are.
      </p>
      <button onClick={onContinue} data-testid="dp-enter-kalki"
        className="mt-10 inline-flex items-center gap-2 px-10 py-4 rounded-full text-[13.5px] font-semibold tracking-[0.14em] uppercase"
        style={{ background: `linear-gradient(135deg, ${INDIGO}, ${FUCHSIA})`, color: "white", boxShadow: `0 20px 50px ${INDIGO}55`, minWidth: 240 }}>
        Enter KALKI →
      </button>
    </motion.div>
  );
}
